<template>
  <div class="mg-wrap">
    <div class="mg-topbar">
      <span class="mg-label">🧠 Memory</span>
      <span class="mg-stat">⏱ {{ timeStr }}</span>
      <span class="mg-stat">✅ {{ matched }}/{{ totalPairs }}</span>
      <span class="mg-stat">❌ {{ misses }}</span>
      <span v-if="running" class="mg-score">Score: <b>{{ score }}</b></span>
    </div>

    <div class="mg-content">
      <!-- win screen -->
      <div v-if="won" class="mg-win">
        <div class="mg-win-emoji">🏆</div>
        <div class="mg-win-title">You Won!</div>
        <div class="mg-win-score">Score: {{ score }}</div>
        <div class="mg-win-time">Time: {{ timeStr }} | Misses: {{ misses }}</div>
        <button class="mg-btn" @click="startGame">↺ Play Again</button>
      </div>

      <!-- start screen -->
      <div v-else-if="!running" class="mg-win">
        <div class="mg-win-emoji">🧠</div>
        <div class="mg-win-title">Memory</div>
        <div class="mg-win-sub">Match all pairs!</div>
        <button class="mg-btn" @click="startGame">▶ Start</button>
      </div>

      <!-- game board -->
      <div v-else class="mg-grid">
        <div
          v-for="(card, i) in cards"
          :key="i"
          class="mg-card"
          :class="{ flipped: card.flipped || card.found, found: card.found, wrong: card.wrong }"
          @click="flip(i)"
        >
          <div class="mg-card-inner">
            <div class="mg-card-front">❓</div>
            <div class="mg-card-back">{{ card.emoji }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const EMOJIS = ['🎮','🎯','🏆','🎲','🎸','🎺','🎻','🎹','🚀','🌟','💎','🔥'];
const PAIRS = 8; // 4x4 grid = 16 cards = 8 pairs

export default {
  name: 'MemoryGame1',
  data() {
    return {
      cards: [],
      flippedIdxs: [],
      matched: 0,
      misses: 0,
      score: 0,
      running: false,
      won: false,
      elapsed: 0,
      timer: null,
      locked: false,
    };
  },
  computed: {
    totalPairs() { return PAIRS; },
    timeStr() {
      const m = Math.floor(this.elapsed / 60).toString().padStart(2, '0');
      const s = (this.elapsed % 60).toString().padStart(2, '0');
      return `${m}:${s}`;
    },
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    startGame() {
      if (this.timer) clearInterval(this.timer);
      this.matched = 0; this.misses = 0; this.score = 0; this.elapsed = 0;
      this.won = false; this.running = true; this.locked = false; this.flippedIdxs = [];
      const emojis = EMOJIS.slice(0, PAIRS);
      const deck = [...emojis, ...emojis].map((e, i) => ({
        emoji: e, flipped: false, found: false, wrong: false, id: i,
      }));
      // shuffle
      for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
      }
      this.cards = deck;
      this.timer = setInterval(() => {
        this.elapsed++;
        this.score = Math.max(0, 1000 - this.elapsed * 2 - this.misses * 5);
        this.$emit('score-update', this.score);
      }, 1000);
    },
    flip(i) {
      if (!this.running || this.locked) return;
      const card = this.cards[i];
      if (card.flipped || card.found) return;
      if (this.flippedIdxs.length === 2) return;

      this.$set(this.cards, i, { ...card, flipped: true });
      this.flippedIdxs.push(i);

      if (this.flippedIdxs.length === 2) {
        this.locked = true;
        const [a, b] = this.flippedIdxs;
        if (this.cards[a].emoji === this.cards[b].emoji) {
          // match
          setTimeout(() => {
            this.$set(this.cards, a, { ...this.cards[a], found: true, flipped: true });
            this.$set(this.cards, b, { ...this.cards[b], found: true, flipped: true });
            this.matched++;
            this.flippedIdxs = [];
            this.locked = false;
            if (this.matched === PAIRS) this.winGame();
          }, 400);
        } else {
          // no match
          this.misses++;
          setTimeout(() => {
            this.$set(this.cards, a, { ...this.cards[a], flipped: false, wrong: false });
            this.$set(this.cards, b, { ...this.cards[b], flipped: false, wrong: false });
            this.flippedIdxs = [];
            this.locked = false;
          }, 900);
        }
      }
    },
    winGame() {
      clearInterval(this.timer);
      this.timer = null;
      this.score = Math.max(0, 1000 - this.elapsed * 2 - this.misses * 5);
      this.running = false;
      this.won = true;
      this.$emit('game-over', this.score);
    },
  },
};
</script>

<style scoped>
.mg-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  min-height: 420px;
}
.mg-topbar {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 16px;
  background: #f8f9ff;
  border-bottom: 1px solid #e8e8f0;
  font-size: 13px;
  color: #333;
  flex-shrink: 0;
}
.mg-label { font-weight: 700; color: #7c5cfc; }
.mg-stat { color: #555; }
.mg-score { margin-left: auto; font-weight: 600; color: #7c5cfc; }
.mg-score b { color: #5b21b6; }

.mg-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow: auto;
}

.mg-win {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.mg-win-emoji { font-size: 3rem; }
.mg-win-title { font-size: 1.5rem; font-weight: 700; color: #1a1a2e; }
.mg-win-score { font-size: 1.2rem; font-weight: 700; color: #7c5cfc; }
.mg-win-time { font-size: .82rem; color: #888; }
.mg-win-sub { font-size: .85rem; color: #888; }
.mg-btn {
  margin-top: 10px;
  background: #7c5cfc;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 28px;
  font-size: .95rem;
  font-weight: 700;
  cursor: pointer;
}
.mg-btn:hover { opacity: .88; }

.mg-grid {
  display: grid;
  grid-template-columns: repeat(4, 80px);
  gap: 10px;
}

.mg-card {
  width: 80px;
  height: 100px;
  cursor: pointer;
  perspective: 600px;
}
.mg-card-inner {
  width: 100%;
  height: 100%;
  position: relative;
  transform-style: preserve-3d;
  transition: transform .4s ease;
  border-radius: 10px;
}
.mg-card.flipped .mg-card-inner,
.mg-card.found .mg-card-inner {
  transform: rotateY(180deg);
}
.mg-card-front,
.mg-card-back {
  position: absolute;
  inset: 0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  user-select: none;
}
.mg-card-front {
  background: linear-gradient(135deg, #7c5cfc, #5b21b6);
  border: 2px solid rgba(124,92,252,.4);
  color: rgba(255,255,255,.7);
  font-size: 1.3rem;
}
.mg-card-back {
  background: #fff;
  border: 2px solid #e8e8f0;
  transform: rotateY(180deg);
  box-shadow: 0 2px 8px rgba(0,0,0,.08);
}
.mg-card.found .mg-card-back {
  background: #f0fdf4;
  border-color: #22c55e;
}
.mg-card:hover:not(.flipped):not(.found) .mg-card-inner {
  transform: scale(1.04);
}

@media (max-width: 480px) {
  .mg-grid {
    grid-template-columns: repeat(4, 62px);
    gap: 6px;
  }
  .mg-card { width: 62px; height: 80px; }
  .mg-card-back, .mg-card-front { font-size: 1.4rem; }
}
</style>
